<template>
  <!-- [sc-49210] Menu a tendina dell'app: pillola con pallino colorato (se
       l'opzione ha un colore) e pannello di opzioni con spunta su quella
       scelta. I <select> nativi non permettono di dare stile alle opzioni. -->
  <div class="app-select" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <button
      ref="trigger"
      type="button"
      class="app-select__trigger"
      :id="id"
      :style="triggerStyle"
      :disabled="disabled"
      :title="title"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="ariaLabel"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span
        v-if="selected && selected.color"
        class="app-select__dot"
        :style="{ background: selected.color }"
      ></span>
      <span
        class="app-select__label"
        :class="{ 'is-placeholder': !selected }"
        >{{ selected ? selected.label : placeholder }}</span
      >
      <i
        class="app-select__chevron fa-solid"
        :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"
        aria-hidden="true"
      ></i>
    </button>

    <!-- Solo per la validazione nativa dei form (required) -->
    <input
      v-if="required"
      class="app-select__native"
      tabindex="-1"
      aria-hidden="true"
      :name="name"
      :value="selected ? String(selected.value) : ''"
      required
      @invalid="onInvalid"
    />

    <!-- Il pannello va nel body: dentro tabelle e modali verrebbe tagliato -->
    <teleport to="body">
      <ul
        v-if="open"
        ref="panel"
        class="app-select__panel"
        :class="{ 'app-select__panel--field': inField }"
        role="listbox"
        :style="panelStyle"
      >
        <li
          v-for="(opt, i) in options"
          :key="String(opt.value)"
          class="app-select__option"
          :class="{
            'is-selected': isSelected(opt),
            'is-active': i === activeIndex,
            'is-disabled': opt.disabled,
          }"
          :style="isSelected(opt) ? selectedStyle(opt) : null"
          role="option"
          :aria-selected="isSelected(opt) ? 'true' : 'false'"
          :aria-disabled="opt.disabled ? 'true' : null"
          @mousedown.prevent
          @mouseenter="activeIndex = i"
          @click="choose(opt)"
        >
          <span
            v-if="opt.color"
            class="app-select__dot"
            :style="{ background: opt.color }"
          ></span>
          <span class="app-select__option-label">{{ opt.label }}</span>
          <i
            v-if="isSelected(opt)"
            class="app-select__check fa-solid fa-check"
            :style="{ color: opt.color || 'currentColor' }"
            aria-hidden="true"
          ></i>
        </li>
      </ul>
    </teleport>
  </div>
</template>

<script>
const NEUTRAL_BORDER = "#d1d5db";

export default {
  name: "AppSelect",
  props: {
    modelValue: { type: [String, Number, Boolean, Object], default: null },
    // [{ value, label, color?, disabled? }]
    options: { type: Array, required: true },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "Seleziona" },
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
    name: { type: String, default: null },
    title: { type: String, default: null },
    ariaLabel: { type: String, default: null },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return { open: false, activeIndex: -1, panelStyle: {}, inField: false };
  },
  computed: {
    selected() {
      return this.options.find((o) => this.isSelected(o)) || null;
    },
    triggerStyle() {
      const color = this.selected?.color || NEUTRAL_BORDER;
      return { borderColor: color };
    },
  },
  beforeUnmount() {
    this.removeListeners();
  },
  methods: {
    isSelected(opt) {
      return opt.value === this.modelValue;
    },
    selectedStyle(opt) {
      return opt.color
        ? { background: `color-mix(in srgb, ${opt.color} 12%, #fff)` }
        : { background: "#f1f5f9" };
    },
    toggle() {
      if (this.open) this.close();
      else this.openPanel();
    },
    openPanel() {
      if (this.disabled || this.open) return;
      const idx = this.options.findIndex((o) => this.isSelected(o));
      this.activeIndex = idx >= 0 ? idx : this.firstEnabled(0, 1);
      // Il pannello è nel body: la misura "da form" va decisa qui
      const trigger = this.$refs.trigger;
      this.inField = Boolean(trigger && trigger.closest(".field"));
      this.position();
      this.open = true;
      document.addEventListener("mousedown", this.onOutside, true);
      window.addEventListener("scroll", this.onViewportChange, true);
      window.addEventListener("resize", this.onViewportChange);
    },
    close() {
      this.open = false;
      this.removeListeners();
    },
    removeListeners() {
      document.removeEventListener("mousedown", this.onOutside, true);
      window.removeEventListener("scroll", this.onViewportChange, true);
      window.removeEventListener("resize", this.onViewportChange);
    },
    position() {
      const el = this.$refs.trigger;
      if (!el || !el.getBoundingClientRect) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const style = {
        left: `${r.left}px`,
        minWidth: `${r.width}px`,
      };
      // Apre verso l'alto se sotto non c'è spazio
      if (spaceBelow < 220 && r.top > spaceBelow) {
        style.bottom = `${window.innerHeight - r.top + 6}px`;
      } else {
        style.top = `${r.bottom + 6}px`;
      }
      this.panelStyle = style;
    },
    onViewportChange(e) {
      // lo scroll dentro il pannello non lo chiude
      if (e && this.$refs.panel && this.$refs.panel.contains(e.target)) return;
      this.close();
    },
    onOutside(e) {
      // $refs e non $el: col commento in testa al template $el non è il div
      const root = this.$refs.trigger?.parentElement;
      const inTrigger = Boolean(root && root.contains(e.target));
      const inPanel = this.$refs.panel && this.$refs.panel.contains(e.target);
      if (!inTrigger && !inPanel) this.close();
    },
    choose(opt) {
      if (!opt || opt.disabled) return;
      this.close();
      this.$refs.trigger?.focus();
      if (this.isSelected(opt)) return;
      this.$emit("update:modelValue", opt.value);
      this.$emit("change", opt.value);
    },
    firstEnabled(from, step) {
      const n = this.options.length;
      for (let k = 0, i = from; k < n; k++, i += step) {
        const j = ((i % n) + n) % n;
        if (!this.options[j].disabled) return j;
      }
      return -1;
    },
    move(step) {
      if (!this.open) {
        this.openPanel();
        return;
      }
      this.activeIndex = this.firstEnabled(this.activeIndex + step, step);
    },
    onKeydown(e) {
      if (this.disabled) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          this.move(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          this.move(-1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (this.open) this.choose(this.options[this.activeIndex]);
          else this.openPanel();
          break;
        case "Escape":
          if (this.open) {
            e.preventDefault();
            this.close();
          }
          break;
        case "Tab":
          this.close();
          break;
      }
    },
    onInvalid() {
      this.$refs.trigger?.focus();
    },
  },
};
</script>

<style>
.app-select {
  position: relative;
  display: inline-flex;
  max-width: 100%;
  font-family: "Nunito Sans", sans-serif;
}

.app-select__trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  /* [sc-49210] alto come i pulsanti principali (.primary-btn: 32px) */
  min-height: 32px;
  padding: 4px 10px;
  border: 2px solid #d1d5db;
  /* come gli altri campi dei form (input: 4px) */
  border-radius: 4px;
  background: #fff;
  color: #1f2937;
  font: inherit;
  /* un filo sotto il testo delle tabelle: con il bordo colorato a 14px
     sembrava più grande dei pulsanti */
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease;
}

.app-select__trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
}

/* Nei form (.field) il menu ha le stesse misure degli input accanto
   (App.css: font 12px, line-height 1.6, padding 4px, bordo 1px). */
.field .app-select__trigger {
  min-height: 0;
  padding: 4px 10px;
  border-width: 1px;
  font-size: 12px;
  line-height: 1.6;
  gap: 6px;
}

.field .app-select__dot {
  width: 10px;
  height: 10px;
}

.field .app-select__chevron {
  font-size: 10px;
}

.app-select.is-disabled .app-select__trigger {
  cursor: not-allowed;
  opacity: 0.6;
}

.app-select__dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.app-select__label {
  /* a tutta larghezza (form su telefono) la freccia resta in fondo a destra */
  flex: 1 1 auto;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-select__label.is-placeholder {
  color: #9ca3af;
}

.app-select__chevron {
  margin-left: 2px;
  font-size: 11px;
  color: #6b7280;
}

/* Campo nativo invisibile sotto la pillola: serve solo a "required" */
.app-select__native {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}

.app-select__panel {
  position: fixed;
  z-index: 10050;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  font-family: "Nunito Sans", sans-serif;
  box-sizing: border-box;
}

.app-select__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  color: #1f2937;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

/* Pannello aperto da un menu dei form: stesso testo degli input (12px) */
.app-select__panel--field .app-select__option {
  gap: 8px;
  padding: 6px 10px;
  font-size: 12px;
}

.app-select__panel--field .app-select__dot {
  width: 10px;
  height: 10px;
}

.app-select__panel--field .app-select__check {
  font-size: 11px;
}

.app-select__option.is-active:not(.is-selected) {
  background: #f5f5f5;
}

.app-select__option.is-disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.app-select__option-label {
  flex: 1;
}

.app-select__check {
  margin-left: 12px;
  font-size: 13px;
}
</style>
